import { getCurrentPage } from '@/api/fbapi';
import { SettingFacebookPageCard } from '@/components/facebook/SettingFacebookPageCard';
import MyContainer from '@/components/MyContainer';
import { BankAccountQR } from '@/components/settings/BankAccountQR';
import { PaymentSettingCard } from '@/components/settings/PaymentSettingCard';
import SchedulerSetting from '@/components/settings/scheduer-setting';
import { ShippingDeliverySetting } from '@/components/settings/ShippingDelivery';
import StoreSettingForm from '@/components/settings/store-setting';
import { LinkButton } from '@/components/ui/buttons/link-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { FbPage } from '@/types/fb-type';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

interface SettingItem {
  key: string;
  value: string;
  type: string;
  logo?: string;
  address2?: string;
  address1?: string;
}

export default function SettingsPage() {
  const { t } = useTranslation(['setting', 'common']);
  const [currentPage, setCurrentPage] = useState<FbPage | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const fetchFacebookPage = async () => {
      const fbPageResponse = await getCurrentPage();
      if (fbPageResponse.success) {
        setCurrentPage(fbPageResponse.data);
      } else {
        toast({
          title: t('common:status.error'),
          description: fbPageResponse.message || t('setting:errors.unexpectedGetCurrentPage'),
          variant: 'destructive',
        });
      }
    };
    fetchFacebookPage();
  }, [toast, t]);

  return (
    <MyContainer
      title={t('title')}
      subtitle={t('description')}
      enableBack={false}
      actions={
        <>
          <LinkButton label={t('Test Detection')} onClick={() => setLocation('/orders-detect')} />
        </>
      }
    >
      <Tabs defaultValue="general">
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch mb-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <TabsList className="mb-0 min-w-max inline-flex h-auto md:h-10">
            <TabsTrigger
              value="general"
              className="text-xs md:text-sm px-2 md:px-3 py-1.5 whitespace-nowrap"
            >
              {t('tabs.general')}
            </TabsTrigger>
            <TabsTrigger
              value="shipping"
              className="text-xs md:text-sm px-2 md:px-3 py-1.5 whitespace-nowrap"
            >
              <span className="hidden sm:inline">{t('tabs.shippingDelivery')}</span>
              <span className="sm:hidden">{t('tabs.shipping')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="payment"
              className="text-xs md:text-sm px-2 md:px-3 py-1.5 whitespace-nowrap"
            >
              {t('tabs.payment')}
            </TabsTrigger>
            <TabsTrigger
              value="accounts"
              className="flex items-center gap-2 md:gap-3 px-2 md:px-4 py-1.5 md:py-2 transition-all text-xs md:text-sm whitespace-nowrap"
            >
              <span className="font-medium hidden sm:inline">{t('tabs.bankAccounts')}</span>
              <span className="font-medium sm:hidden">{t('tabs.bank')}</span>
              <div className="hidden md:flex items-center border-l pl-3 border-slate-200">
                <img
                  src="/khqr.png"
                  alt="KHQR"
                  className="h-5 w-auto object-contain brightness-110 shadow-sm rounded-sm"
                />
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="facebook_page"
              className="text-xs md:text-sm px-2 md:px-3 py-1.5 whitespace-nowrap"
            >
              <span className="hidden sm:inline">{t('tabs.facebookPage')}</span>
              <span className="sm:hidden">{t('tabs.facebook')}</span>
            </TabsTrigger>
            <TabsTrigger
              value="scheduler"
              className="text-xs md:text-sm px-2 md:px-3 py-1.5 whitespace-nowrap"
            >
              <span className="hidden sm:inline">{t('tabs.scheduler')}</span>
              <span className="sm:hidden">{t('tabs.schedule')}</span>
            </TabsTrigger>
            {/* <TabsTrigger
              value="usdt"
              className="text-xs md:text-sm px-2 md:px-3 py-1.5 whitespace-nowrap"
            >
              <span className="hidden sm:inline">USDT</span>
              <span className="sm:hidden">USDT</span>
            </TabsTrigger> */}
          </TabsList>
        </div>

        <TabsContent value="general" className="mt-2 md:mt-4">
          <StoreSettingForm />
        </TabsContent>
        <TabsContent value="shipping" className="mt-2 md:mt-4">
          <ShippingDeliverySetting />
        </TabsContent>

        <TabsContent value="payment" className="mt-2 md:mt-4">
          <PaymentSettingCard />
        </TabsContent>

        <TabsContent value="accounts" className="mt-2 md:mt-4">
          <BankAccountQR />
        </TabsContent>
        <TabsContent value="facebook_page" className="mt-2 md:mt-4">
          <SettingFacebookPageCard currentPage={currentPage} />
        </TabsContent>
        <TabsContent value="scheduler" className="mt-2 md:mt-4">
          <SchedulerSetting />
        </TabsContent>
        {/* <TabsContent value="usdt" className="mt-2 md:mt-4">
          <UsdtTradePage />
        </TabsContent> */}
      </Tabs>
    </MyContainer>
  );
}
